import moment, { Moment } from 'moment-timezone';
import { DepositBankAccount } from './deposit-bank-account';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { FixedAsset } from './accounting/fixed-asset';
import { PlaidToken } from './plaid-token';
import { Organization } from './organization';
import { AllianceCredential } from './alliance/alliance-credential';
import { BankReconciliation } from './bank-reconciliation';
import { LobBankAccount } from './lob-bank-account';

export interface HistoricalBalance
{
    createdAt: string;
    balance: number;
}

export class UnifiedBankAccount
{
    id: number;
    organizationId: number;
    friendlyName: string;
    routingNumber: string;
    accountNumber: string;
    last4: string;
    country: string;
    currency: string;
    plaidAccountId: string;
    plaidTokenId: number;
    plaidBalance: number;
    fcBalance: number;
    /**
     * SD-8906: set by core when a Financial Connections account's connection has been severed at
     * Stripe and can never recover on its own, the account holder has to re-authorize, because a
     * disconnected `fca_` cannot be revived and re-linking mints a new one. While this is set, the
     * account still has an `fcBalance`, but it is FROZEN at whatever it was when the feed died, and
     * transaction import is permanently excluded from the hourly run.
     */
    fcReconnectRequiredAt: Moment;
    /**
     * SD-9845: when core last successfully refreshed `fcBalance` from Stripe — i.e. how old the
     * figure on screen actually is. Already served by the same payload (`fc_balance_refreshed_at`
     * is not in the model's `$hidden`, and `CamelCaseResponseKeys` camelCases every response key);
     * it was simply never mapped here.
     *
     * Read by `bankBalanceUpdatesPausedTooltip()` for the state SD-9584 creates and nothing
     * previously disclosed: the CTA is withheld for a transient-inactive connection while the
     * balance beside it is frozen at whatever it was when the feed died.
     */
    fcBalanceRefreshedAt: Moment;
    /**
     * SD-9350 / SD-10504: what STRIPE last told us about the connection ('active', 'inactive',
     * 'disconnected'), as opposed to our own verdict about it.
     *
     * ⚠️ It LAGS — it is written on each observation, so up to an hourly import behind reality.
     * Safe for what the delete dialog uses it for (disclosure, and hiding an action), never as an
     * authority: the delete guard re-reads the connection at Stripe server-side and refuses there.
     */
    fcStatus: string;
    /**
     * SD-10744: Stripe's `status_details.inactive.action` / `.cause`, stored by core since SD-10334.
     * Null on every healthy row by design (Stripe serves them only for inactive accounts). The
     * Financial Accounts dashboard reads `none` to show the contact-support notice after a failed
     * reconnect (SD-10334 option B).
     */
    fcStatusDetailsAction: string | null;
    fcStatusDetailsCause: string | null;
    /**
     * SD-9817 / SD-10504: when the reconnect marker last CLEARED — i.e. when this connection last
     * came back.
     *
     * ⚠️ NULL does NOT mean stale. It is null on ~71% of live connected rows (81 of 275, measured
     * 2026-09-29) because those connections NEVER BROKE and so never recovered. Never read a null
     * here as evidence that a connection is dead.
     */
    fcLastRecoveredAt: Moment;
    plaidImportAfter: Moment;
    sourceType: string;
    stripeFinancialConnectionsId: string;
    stripePaymentMethodId: string;
    createdAt: Moment;
    updatedAt: Moment;
    deletedAt: Moment;

    isTreasuryAccount: boolean = false;
    label: string;
    name: string;

    depositBankAccount: DepositBankAccount = null;
    fixedAsset: FixedAsset = null;
    plaidToken: PlaidToken;
    plaidAccount: {
        accountId: string;
        mask: string;
        name: string;
        officialName: string;
        type: string;
        subtype: string;
    };
    isAllianceAccount: boolean;
    /**
     * SD-8719: whether core can actually READ this account's balance and transactions, appended by
     * UnifiedBankAccount::appendRelatedFields(). Null when the field is absent from the response,
     * so callers can tell "core did not say" apart from a real `false`.
     */
    isBalanceLinked: boolean;
    /**
     * SD-9632: whether `fcBalance` is a figure the BANK actually reported, appended by
     * UnifiedBankAccount::appendRelatedFields().
     *
     * Answers a different question from `isBalanceLinked` above, and the difference is the whole
     * point: a properly linked, un-frozen account can still be holding a balance Stripe never
     * computed. Core stores a literal `0` both for a genuinely empty account and for one whose
     * balance had not been computed yet, so `fcBalance` alone cannot tell a true $0.00 from a
     * figure we never obtained, which is how 12 live orgs came to display a confident $0.00,
     * one of them on a reserve account.
     *
     * Read it the same way as its siblings: null when the field is absent, so a UI bundle running
     * against a core that predates the append falls back to rendering the figure rather than
     * treating `undefined` as unconfirmed and hiding every balance.
     *
     * ⚠️ A NON-ZERO balance reads as confirmed even with no bank timestamp, because a positive
     * figure can only have come from a balance the bank reported, core's accessor carries the same
     * carve-out, and it is what let this ship without a backfill. So "no timestamp" does NOT imply
     * "pending"; only an unproven ZERO does (review F-3977 caught the inverse claim in three
     * comments on the core side).
     *
     * Typed `boolean | null` to match what the constructor actually assigns (review F-4da4).
     */
    isBalanceConfirmed: boolean | null;
    /**
     * SD-10133: whether re-linking THIS connection can ever work, appended by
     * UnifiedBankAccount::appendRelatedFields(). Null when the field is absent from the response,
     * read by consumers as NOT terminal so an older core keeps today's behaviour.
     */
    isConnectionTerminal: boolean | null;
    /**
     * SD-8671: whether core can actually MOVE MONEY on this account, appended by
     * UnifiedBankAccount::appendRelatedFields(). Sibling of `isBalanceLinked` above and read the
     * same way — null when the field is absent from the response, so a UI bundle running against a
     * core that predates the append can fall back rather than treat `undefined` as ineligible.
     * Reading it is what stops the transfer pickers inferring eligibility from which Stripe ids
     * happen to be stored, the inference that silently hid a cohort measured at 17 live rows on
     * 2026-08-19 (provenance: core's `FinancialConnectionsService::findAdoptableExternalAccount()`,
     * which carries the query — a dated sample, not an invariant).
     */
    isTransferEligible: boolean;
    /**
     * SD-9540: the same question for the "To" side only, which is a weaker requirement.
     *
     * `isTransferEligible` governs both directions. This one additionally admits a Connect-created
     * external account Stripe holds only as a `ba_`, because outbound accepts a BankAccount id
     * directly and needs no mandate, while debiting one inbound does. It has to be declared AND
     * assigned here or the flag dies at this boundary: every consumer builds its accounts through
     * `new UnifiedBankAccount(...)`, so an undeclared field silently reads `undefined` downstream and
     * the To picker falls back to the narrower answer — leaving the exact cohort this exists for
     * disabled, with core computing and serialising a flag nobody ever sees.
     */
    isOutboundEligible: boolean;
    /**
     * SD-9078: set while Stripe is waiting for the two small deposits on a manually entered
     * account to be confirmed. Until then Stripe refuses to move money on it, so both transfer
     * guards hold it and the UI names that as the reason rather than showing a bare "unavailable".
     */
    microdepositVerificationRequiredAt: Moment;
    allianceCredential: AllianceCredential;
    allianceTransactionImportEnabled: boolean;
    allianceTransactionImportAfter: Moment;
    unreviewedTransactionsCount: number;
    organization: Organization;
    plaidBalanceHistory: HistoricalBalance[] = [];
    reconciliations: BankReconciliation[];
    lobBankAccounts: LobBankAccount[];

    constructor(unifiedBankAccount: any) {
        const timezone = LegFiJwtService.getTimezone();

        this.id = unifiedBankAccount.id || null;
        this.organizationId = unifiedBankAccount.organizationId || null;
        this.friendlyName = unifiedBankAccount.friendlyName || '';
        this.routingNumber = unifiedBankAccount.routingNumber || '';
        this.accountNumber = unifiedBankAccount.accountNumber || '';
        this.last4 = unifiedBankAccount.last4 || '';
        this.label = this.friendlyName?.trim()
            ? this.friendlyName
            : (this.last4 ? `Account ••••${this.last4}` : 'Bank account');
        this.name = this.label;
        this.country = unifiedBankAccount.country ?? 'US';
        this.currency = unifiedBankAccount.currency ?? 'usd';
        this.unreviewedTransactionsCount = unifiedBankAccount.unreviewedTransactionsCount || 0;
        this.sourceType = unifiedBankAccount.sourceType || null;
        this.isTreasuryAccount = !!unifiedBankAccount.isTreasuryAccount;
        this.stripeFinancialConnectionsId = unifiedBankAccount.stripeFinancialConnectionsId || null;
        this.stripePaymentMethodId = unifiedBankAccount.stripePaymentMethodId || null;
        this.plaidAccountId = unifiedBankAccount.plaidAccountId || null;
        this.plaidAccount = unifiedBankAccount.plaidAccount || null;
        this.plaidTokenId = unifiedBankAccount.plaidTokenId || null;
        this.plaidToken = unifiedBankAccount.plaidToken ? new PlaidToken(unifiedBankAccount.plaidToken) : null;
        this.plaidImportAfter = unifiedBankAccount.plaidImportAfter
                ? moment.utc(unifiedBankAccount.plaidImportAfter, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        this.fcReconnectRequiredAt = unifiedBankAccount.fcReconnectRequiredAt
                ? moment.utc(unifiedBankAccount.fcReconnectRequiredAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        this.fcBalanceRefreshedAt = unifiedBankAccount.fcBalanceRefreshedAt
                ? moment.utc(unifiedBankAccount.fcBalanceRefreshedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        this.fcStatus = unifiedBankAccount.fcStatus || null;
        this.fcStatusDetailsAction = unifiedBankAccount.fcStatusDetailsAction ?? unifiedBankAccount.fc_status_details_action ?? null;
        this.fcStatusDetailsCause = unifiedBankAccount.fcStatusDetailsCause ?? unifiedBankAccount.fc_status_details_cause ?? null;

        this.fcLastRecoveredAt = unifiedBankAccount.fcLastRecoveredAt
                ? moment.utc(unifiedBankAccount.fcLastRecoveredAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        this.createdAt = unifiedBankAccount.createdAt
                ? moment.utc(unifiedBankAccount.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;
        this.updatedAt = unifiedBankAccount.updatedAt
                ? moment.utc(unifiedBankAccount.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;
        this.deletedAt = unifiedBankAccount.deletedAt
                ? moment.utc(unifiedBankAccount.deletedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        if (unifiedBankAccount.depositBankAccount) {
            this.depositBankAccount = new DepositBankAccount(unifiedBankAccount.depositBankAccount);
            this.depositBankAccount.unifiedBankAccount = this;
        }
        if (unifiedBankAccount.fixedAsset) {
            this.fixedAsset = new FixedAsset(unifiedBankAccount.fixedAsset);
            this.fixedAsset.owner = this;
        }
        this.organization = unifiedBankAccount.organization ? new Organization(unifiedBankAccount.organization) : null;
        this.isAllianceAccount = unifiedBankAccount.isAllianceAccount ?? false;
        this.allianceCredential = unifiedBankAccount.allianceCredential
                ? new AllianceCredential(unifiedBankAccount.allianceCredential)
                : null;
        this.allianceTransactionImportEnabled = (this.isAllianceAccount && unifiedBankAccount.allianceTransactionImportEnabled) ?? false;
        this.allianceTransactionImportAfter = unifiedBankAccount.allianceTransactionImportAfter
                ? moment.utc(unifiedBankAccount.allianceTransactionImportAfter, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;
        if (unifiedBankAccount.plaidBalanceHistory) {
            this.plaidBalanceHistory = unifiedBankAccount.plaidBalanceHistory;
        }
        if (unifiedBankAccount.reconciliations) {
            this.reconciliations = unifiedBankAccount.reconciliations.map((reconciliation: any) => new BankReconciliation(reconciliation));
        }

        // plaidBalance is a database column that's always returned, regardless of
        // whether plaidToken relationship is loaded. Don't gate it on plaidToken presence.
        this.plaidBalance = unifiedBankAccount.plaidBalance ?? null;
        this.fcBalance = unifiedBankAccount.fcBalance ?? null;
        this.isBalanceLinked = unifiedBankAccount.isBalanceLinked ?? null;
        this.isConnectionTerminal = unifiedBankAccount.isConnectionTerminal ?? null;
        // SD-9632: null (not false) when core did not say, so an older core keeps rendering the
        // figure instead of every balance silently reading as unconfirmed. Same fallback contract
        // as the three sibling flags around it.
        this.isBalanceConfirmed = unifiedBankAccount.isBalanceConfirmed ?? null;
        this.isTransferEligible = unifiedBankAccount.isTransferEligible ?? null;
        this.isOutboundEligible = unifiedBankAccount.isOutboundEligible ?? null;
        this.microdepositVerificationRequiredAt = unifiedBankAccount.microdepositVerificationRequiredAt
                ? moment.utc(unifiedBankAccount.microdepositVerificationRequiredAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        if (unifiedBankAccount.lobBankAccounts) {
            this.lobBankAccounts = unifiedBankAccount.lobBankAccounts.map(lba => new LobBankAccount(lba));
        }
    }
}
